import { memo, useEffect, useRef, useState } from 'react';
import { ModalCloseSvg } from '@/sub-luca-plugin/compatible-components/svg-icons';
import classNames from 'classnames';
import { IProjectSelectProps, ITabData, ITwoLevelSubjectItem, ISubjectItem, IProjectItem, TReturnType, IListItem } from '../helper';
import { ITwoLevelSubjectConfig, ISubjectConfig, IProjectConfig, IProjectIds } from '@/sub-luca-plugin/types';
import './index.scss';

const ProjectSelect = ({
    projects,
    determinedIds = {},
    allowChangeDeterminedIds,
    returnType = 'config',
    className,
    style,
    onReturnConfig,
    onReturnProjectConfig,
    onClose,
}: IProjectSelectProps) => {
    // const lastId = useRef(-1);
    const [tabDatas, setTabDatas] = useState<ITabData[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const currentTabData = tabDatas[activeIndex];

    const transformTwoLevelSubjectTabData = (data: ITwoLevelSubjectItem[], ids?: IProjectIds, isDetermined = false) => {
        const { twoLevelSubjectId } = ids || {};
        let tabDataOfTwoLevelSubject: ITabData = { name: '请选择', list: [] };
        data.forEach(item => {
            if (item.tsid === twoLevelSubjectId) {
                tabDataOfTwoLevelSubject = {
                    tsid: item.tsid,
                    name: item.name,
                    config: item.config,
                    list: [...tabDataOfTwoLevelSubject.list, { ...item, selected: true }],
                    isDetermined,
                };
            } else {
                tabDataOfTwoLevelSubject.list.push({ ...item, selected: false });
            }
        });
        return [tabDataOfTwoLevelSubject];
    };

    const transformSubjectTabData = (data: ISubjectItem[], ids?: IProjectIds, isDetermined = false) => {
        const { subjectId } = ids || {};
        let tabDataOfSubject: ITabData = { name: '请选择', list: [] };
        let tlSubjects: ITwoLevelSubjectConfig[] = [];
        data.forEach(item => {
            if (item.sid === subjectId) {
                tabDataOfSubject = {
                    sid: item.sid,
                    name: item.name,
                    config: item.config,
                    list: [...tabDataOfSubject.list, { ...item, selected: true }],
                    isDetermined,
                };
                tlSubjects = item.tlSubjects || [];
            } else {
                tabDataOfSubject.list.push({ ...item, selected: false });
            }
        });
        if (tlSubjects.length) {
            return [tabDataOfSubject, ...transformTwoLevelSubjectTabData(tlSubjects, ids, isDetermined)];
        }
        return [tabDataOfSubject];
    };

    const transformProjectTabData = (data: IProjectItem[], ids?: IProjectIds, isDetermined = false) => {
        const { projectId } = ids || {};
        let tabDataOfProject: ITabData = { name: '请选择', list: [] };
        let subjects: ISubjectConfig[] = [];
        data.forEach(item => {
            if (item.pid === projectId) {
                tabDataOfProject = {
                    ...tabDataOfProject,
                    pid: item.pid,
                    name: item.name,
                    config: item.config,
                    list: [...tabDataOfProject.list, { ...item, selected: true }],
                    isDetermined,
                    aiAskOnOff: item.aiAskOnOff,
                };
                subjects = item.subjects || [];
            } else {
                tabDataOfProject.list.push({ ...item, selected: false });
            }
        });
        if (subjects.length) {
            return [tabDataOfProject, ...transformSubjectTabData(subjects, ids, isDetermined)];
        }
        return [tabDataOfProject];
    };

    const isLastChild = (data: ITabData[]): any => {
        const lastData = data[data.length - 1];
        if (lastData['tsid']) return lastData;
        const pid = lastData['pid'];
        if (pid && lastData.list.some(item => (item as IProjectItem).pid === pid && !(item as IProjectItem).subjects?.length)) {
            return lastData;
        }
        const sid = lastData['sid'];
        if (sid && lastData.list.some(item => (item as ISubjectItem).sid === sid && !(item as ISubjectItem).tlSubjects?.length)) {
            return lastData;
        }
        return null;
    };

    const initData = async (projects: IProjectConfig[], ids: IProjectIds, returnType: TReturnType, isDetermined = false) => {
        const newTabData = transformProjectTabData(projects, ids, isDetermined);
        const lastChildData = isLastChild(newTabData);
        if (returnType === 'subjects' && newTabData[0].pid) {
            const projectConfig = newTabData[0];
            onReturnProjectConfig?.({
                projectId: projectConfig.pid!,
                projectName: projectConfig.name!,
                subjects: (projectConfig.list.find(item => !!item.selected) as IProjectItem)?.subjects || [],
                aiAskOnOff: projectConfig.aiAskOnOff!,
            });
        } else if (returnType === 'config' && lastChildData) {
            onReturnConfig?.({
                ...(lastChildData?.config || {}),
                projectId: newTabData[0]?.pid,
                projectName: newTabData[0]?.name,
                subjectId: newTabData[1]?.sid,
                subjectName: newTabData[1]?.name,
                twoLevelSubjectId: newTabData[2]?.tsid,
                twoLevelSubjectName: newTabData[2]?.name,
            });
        } else {
            setTabDatas(newTabData);
            setActiveIndex(newTabData.length - 1);
        }
    };

    const handleJudgeTabData = (tabData: ITabData[]) => {
        // console.log(data, sliceIndex);
        const lastChildData = isLastChild(tabData);
        if (lastChildData) {
            onReturnConfig?.({
                ...(lastChildData?.config || {}),
                projectId: tabData[0]?.pid,
                projectName: tabData[0]?.name,
                subjectId: tabData[1]?.sid,
                subjectName: tabData[1]?.name,
                twoLevelSubjectId: tabData[2]?.tsid,
                twoLevelSubjectName: tabData[2]?.name,
            });
        } else {
            setTabDatas(tabData);
            setActiveIndex(index => index + 1);
        }
    };

    const handleCategoryChange = (item: IListItem, tabDatas: ITabData[], activeIndex: number) => {
        const list = tabDatas[activeIndex].list;
        if ('pid' in item) {
            const data = transformProjectTabData(list as IProjectItem[], { projectId: item.pid });
            handleJudgeTabData(data);
        } else if ('sid' in item) {
            const data = transformSubjectTabData(list as ISubjectItem[], { subjectId: item.sid });
            const newData = tabDatas.slice(0, 1).concat(data);
            handleJudgeTabData(newData);
        } else if ('tsid' in item) {
            const data = transformTwoLevelSubjectTabData(list as ITwoLevelSubjectItem[], { twoLevelSubjectId: item.tsid });
            const newData = tabDatas.slice(0, 2).concat(data);
            handleJudgeTabData(newData);
        }
    };

    // 阻止在选择项目科目上重复选择
    // const isRepeatSelect = (item: IListItem) => {
    //     if ('pid' in item) {
    //         const result = item.pid === lastId.current;
    //         lastId.current = item.pid;
    //         return result;
    //     }
    //     if ('sid' in item) {
    //         const result = item.sid === lastId.current;
    //         lastId.current = item.sid;
    //         return result;
    //     }
    //     if ('tsid' in item) {
    //         const result = item.tsid === lastId.current;
    //         lastId.current = item.tsid;
    //         return result;
    //     }
    //     return false;
    // };

    const handleCategoryClick = (returnType: TReturnType, item: IListItem, tabDatas: ITabData[], activeIndex: number) => {
        // if (isRepeatSelect(item)) return;
        if (returnType === 'config') {
            handleCategoryChange(item, tabDatas, activeIndex);
        } else if (returnType === 'subjects' && 'pid' in item) {
            onReturnProjectConfig?.({
                projectId: item.pid,
                projectName: item.name,
                subjects: item.subjects,
                aiAskOnOff: item.aiAskOnOff,
            });
        }
    };

    useEffect(() => {
        if (projects?.length) {
            // console.log('determinedIds', determinedIds);
            initData(projects, determinedIds, returnType, !allowChangeDeterminedIds);
        }
    }, []);

    return (
        <div className={classNames('project-select-select', className)} style={style}>
            <div className="project-select-select__title">答疑学科</div>
            {onClose && <ModalCloseSvg className="project-select-select__close" onClick={onClose} />}
            <div className="project-select-select__tabs">
                {tabDatas.map((item, index) => {
                    const key = index.toString();
                    return (
                        <div
                            key={key}
                            className={classNames(
                                'project-select-select__tab',
                                index === activeIndex && 'project-select-select__tab--active'
                            )}
                            onClick={() => !item.isDetermined && setActiveIndex(index)}
                        >
                            {item.name}
                        </div>
                    );
                })}
            </div>
            {!!currentTabData?.list.length && (
                <div className="project-select-select__categories-contaier">
                    <div className="project-select-select__categories">
                        {currentTabData.list.map((item, index) => {
                            const key = index.toString();
                            return (
                                <div
                                    key={key}
                                    className={classNames(
                                        'project-select-select__category',
                                        item.selected && 'project-select-select__category--selected'
                                    )}
                                    onClick={() => handleCategoryClick(returnType, item, tabDatas, activeIndex)}
                                >
                                    {item.name}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(ProjectSelect);
